import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Select, MenuItem, TextField, Button, InputAdornment } from '@mui/material';
import { updateUserRole, sendVerificationCode, verifyPhone, updateUserEmail, updateUserPhone } from '../../utils/api';
import PhoneIcon from '@mui/icons-material/Phone';
import emailjs from '@emailjs/browser';

const SelectRole = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const navigate = useNavigate();
    const [role, setRole] = useState('trainee');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
    const [needsPhoneVerification, setNeedsPhoneVerification] = useState(false);
    const [needsPhoneInput, setNeedsPhoneInput] = useState(false);
    const [phone, setPhone] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [user, setUser] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code) {
            setError('Code is required');
            return;
        }
        try {
            const response = await updateUserRole({ userId, role, code });
            if (response.data) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('userId', response.data.user._id);
                localStorage.setItem('username', response.data.user.username);
                localStorage.setItem('role', response.data.user.role);
                localStorage.setItem('googleId', response.data.user.googleId || null);
                localStorage.setItem('email', response.data.user.email || null);
                localStorage.setItem('phone', response.data.user.phone || null);

                const userData = response.data.user;
                setUser(userData);
                setNeedsEmailVerification(!userData.emailVerified);
                setNeedsPhoneVerification(!userData.phone || !userData.phoneVerified);
                setNeedsPhoneInput(!userData.phone);

                if (!userData.emailVerified || !userData.phone || !userData.phoneVerified) {
                    setIsVerifying(true);
                } else {
                    navigate('/');
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to update role. Please try again.';
            setError(errorMessage);
        }
    };

    const handleSendCodes = async () => {
        if (!phone) {
            setVerificationError('Phone number is required');
            return;
        }
        if (!/^\+?[1-9]\d{1,14}$/.test(phone)) {
            setVerificationError('Please enter a valid phone number');
            return;
        }
        try {
            await sendVerificationCode(userId, phone);
            localStorage.setItem('phone', phone);

            if (needsEmailVerification) {
                const emailVerificationCode = Math.floor(100000 + Math.random() * 900000).toString();
                const templateParams = {
                    name: user.username,
                    email: user.email,
                    message: `Your email verification code is ${emailVerificationCode}`,
                };
                await emailjs.send('service_46bv0oq', 'template_qedmi1r', templateParams, 'yH0mz21D4OHFGHtEf');
                localStorage.setItem('emailVerificationCode', emailVerificationCode);
            }

            setNeedsPhoneInput(false);
        } catch (error) {
            console.error('Error sending verification codes:', error);
            setVerificationError('Failed to send verification codes');
        }
    };

    useEffect(() => {
        if (isVerifying && !needsPhoneInput && user) {
            const sendCodes = async () => {
                try {
                    if (needsEmailVerification) {
                        const emailVerificationCode = Math.floor(100000 + Math.random() * 900000).toString();
                        const templateParams = {
                            name: user.username,
                            email: user.email,
                            message: `Your email verification code is ${emailVerificationCode}`,
                        };
                        await emailjs.send('service_46bv0oq', 'template_qedmi1r', templateParams, 'yH0mz21D4OHFGHtEf');
                        localStorage.setItem('emailVerificationCode', emailVerificationCode);
                    }
                    if (needsPhoneVerification && user.phone) {
                        await sendVerificationCode(userId, user.phone);
                    }
                } catch (error) {
                    console.error('Error sending verification codes:', error);
                    setVerificationError('Failed to send verification codes');
                }
            };
            sendCodes();
        }
    }, [isVerifying, needsPhoneInput, needsEmailVerification, needsPhoneVerification, userId, user]);

    const handleVerify = async () => {
        let hasError = false;
        if (needsEmailVerification) {
            const storedEmailCode = localStorage.getItem('emailVerificationCode');
            if (emailCode !== storedEmailCode) {
                setVerificationError('Invalid email verification code');
                hasError = true;
            } else {
                try {
                    await updateUserEmail(userId);
                    setNeedsEmailVerification(false);
                    localStorage.removeItem('emailVerificationCode');
                } catch (error) {
                    setVerificationError('Failed to verify email');
                    hasError = true;
                }
            }
        }
        if (needsPhoneVerification && !needsPhoneInput) {
            try {
                const response = await verifyPhone(userId, phoneCode, phone || user.phone);
                if (response.message !== 'Phone verified successfully') {
                    setVerificationError('Invalid phone verification code');
                    hasError = true;
                } else {
                    setNeedsPhoneVerification(false);
                }
            } catch (error) {
                setVerificationError('Failed to verify phone');
                hasError = true;
            }
        }
        if (!hasError) {
            navigate('/');
        }
    };

    if (isVerifying) {
        return (
            <Box sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 5 }}>
                <Typography textAlign="right" direction="rtl" color="#B5A78A" fontFamily="GveretLevin" variant="h6" mb={2}>
                    אימות נדרש
                </Typography>
                <Typography textAlign="right" direction="rtl" fontFamily="GveretLevin">
                    יש להזין את המספרי קוד שנשלחו למייל ו/או לטלפון
                </Typography>
                {needsPhoneInput ? (
                    <Box>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="מספר טלפון"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button onClick={handleSendCodes} variant="contained" sx={{ mt: 2 }}>
                            שלח קודים
                        </Button>
                    </Box>
                ) : (
                    <>
                        {needsEmailVerification && (
                            <TextField
                                fullWidth
                                margin="normal"
                                label="קוד למייל"
                                value={emailCode}
                                onChange={(e) => setEmailCode(e.target.value)}
                                required
                            />
                        )}
                        {needsPhoneVerification && (
                            <TextField
                                fullWidth
                                margin="normal"
                                label="קוד לטלפון"
                                value={phoneCode}
                                onChange={(e) => setPhoneCode(e.target.value)}
                                required
                            />
                        )}
                        <Button
                            onClick={handleVerify}
                            variant="contained"
                            fullWidth
                            sx={{
                                backgroundColor: '#C4A484',
                                '&:hover': { backgroundColor: '#C4A484' },
                                mt: 2,
                            }}
                        >
                            אמת
                        </Button>
                    </>
                )}
                {verificationError && <Typography color="error">{verificationError}</Typography>}
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 5 }}>
            <Typography textAlign="right" direction="rtl" color="#B5A78A" fontFamily="GveretLevin" variant="h6" mb={2}>
                בחירת תפקיד
            </Typography>
            <form onSubmit={handleSubmit} textAlign="right" direction="rtl">
                <Select
                    fullWidth
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    sx={{ mb: 2, textAlign: 'right', direction: 'rtl' }}
                >
                    <MenuItem fontFamily="GveretLevin" value="trainee">מתאמן</MenuItem>
                    <MenuItem value="trainer">מאמן</MenuItem>
                </Select>
                <TextField
                    fullWidth
                    textAlign="right"
                    justifyContent="right"
                    direction="rtl"
                    label={role === 'trainee' ? 'קוד מאמן' : 'קוד רישום '}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    sx={{ mb: 2, textAlign: 'right', direction: 'rtl' }}
                />
                {error && <Typography color="error">{error}</Typography>}
                <Button
                    sx={{
                        backgroundColor: '#C4A484',
                        '&:hover': { backgroundColor: '#C4A484' },
                    }}
                    type="submit"
                    variant="contained"
                    fullWidth
                >
                    אישור
                </Button>
            </form>
        </Box>
    );
};

export default SelectRole;